import React, { useEffect, useRef, useState } from "react"
import styles from "./Files.module.scss"
import axios from "axios"
import { API_ROUTE, getFileColor } from "../../../../utils"
import { UserInterface } from "../../../../App"
import toast from "react-hot-toast"
import OptionsMenu from "./OptionsMenu/OptionsMenu"
import {
    bytesToReadable,
    filterTree,
    findParentFolder,
    readableFileDate,
    sortTree,
    truncateFileName,
} from "./helpers"

export interface FileItem {
    _id: string
    type: "file" | "folder"
    name: string
    parentId?: string
    owner?: string
    created: number
    lastModified: number
    size?: number
    contentType?: string
    fileId?: string
    s3Url?: string
    originalS3Url?: string
    children?: FileItem[]
}

const Files: React.FC<{
    newAppSpace: any
    setActiveAppSpace: any
    setUser: any
    user: UserInterface
    createFile: string
    uploading: boolean
    setUploading?: any
    readMode?: boolean
    fileTree?: FileItem[]
    setFileTree?: React.Dispatch<React.SetStateAction<FileItem[]>>
}> = ({
    newAppSpace,
    setActiveAppSpace,
    setUser,
    user,
    createFile,
    uploading,
    setUploading,
    readMode,
    fileTree = [],
    setFileTree = () => {}, // Default to a no-op function if not provided
}) => {
    const fileUploadRef = useRef<HTMLInputElement>(null)
    const [search, setSearch] = useState<string>("")
    const [showOptions, setShowOptions] = useState<{ id: string; x: number; y: number } | null>(
        null
    )
    const [renameItemId, setRenameItemId] = useState<string | null>(null)
    const [newName, setNewName] = useState<string>("")
    const [dragItemId, setDragItemId] = useState<string | null>(null)
    const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set())

    const fetchFileTree = async () => {
        try {
            const response = await axios.get(`${API_ROUTE}/files/get-file-tree`, {
                params: { owner: user._id, authToken: user.authToken },
            })
            setFileTree(response.data.tree)
        } catch {
            toast.error("Failed to load files. Please re-authenticate.")

            // log the user out if the error is related to authentication
            setUser({} as UserInterface)
            localStorage.removeItem("credentials")
            window.location.href = "#/login"
        }
    }

    useEffect(() => {
        if (user._id && user.rootFolderId) {
            fetchFileTree()
        }
    }, [user._id, user.rootFolderId])

    async function uploadFileAndUpdateUser(file: any, content: any, originalContent?: string) {
        setUploading(true)
        try {
            const payload = {
                owner: user._id,
                authToken: user.authToken,
                name: file.name,
                size: file.size,
                mimeType: file.mimeType,
                lastModified: file.lastModified || Date.now(),
                created: file.created || Date.now(),
                content,
                parentId: user.rootFolderId,
                contentType: file.contentType,
                originalContent,
            }

            const response = await axios.post(`${API_ROUTE}/files/upload-file`, payload)
            const renew = await axios.post(`${API_ROUTE}/auth/verify-user`, {
                _id: response.data._id,
                authToken: response.data.authToken,
            })
            setUser(renew.data)
            newAppSpace({ ...file, fileId: response.data.fileId, _id: response.data.fileId })
            fetchFileTree()
        } catch (error) {
            console.error("Upload file error:", error)
            toast.error("File upload failed")
        } finally {
            setUploading(false)
        }
    }

    const createNewFile = async (contentType: string) => {
        // doc or sheet, but it's really either text/html or application/json
        const mimeType = contentType === "doc" ? "text/html" : "application/json"
        const file = {
            owner: user._id,
            authToken: user.authToken,
            name: contentType === "doc" ? "Document" : "Spreadsheet",
            size: 10,
            mimeType,
            contentType: mimeType,
            lastModified: Date.now(),
            created: Date.now(),
        }
        const content = `data:${mimeType};base64,${
            contentType === "doc" ? btoa("<h1>Document</h1>") : btoa("{}")
        }`
        uploadFileAndUpdateUser(file, content)
    }

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0]
        if (file) {
            // Parsing of OFFICE files
            if (file?.type.startsWith("application/vnd.openxmlformats-officedocument")) {
                // deals with .docx and .xlsx files
                if (!file.type.endsWith(".document") && !file.type.endsWith(".sheet")) {
                    toast.error("Unsupported office file type.")
                    return
                }

                const reader = new FileReader()
                reader.onload = async (e) => {
                    const originalContent = e.target?.result as string

                    const fileData = {
                        owner: user._id,
                        authToken: user.authToken,
                        name: file.name,
                        size: file.size,
                        mimeType: file.type,
                        contentType: file?.type.endsWith(".document")
                            ? "text/html"
                            : "application/json",
                        lastModified: file.lastModified,
                        created: Date.now(),
                    }
                    uploadFileAndUpdateUser(fileData, originalContent, originalContent)
                }
                reader.readAsDataURL(file)
                return
            }

            // Regular file upload
            const reader = new FileReader()
            reader.onload = async (e) => {
                try {
                    const fileData = {
                        owner: user._id,
                        authToken: user.authToken,
                        name: file.name,
                        size: file.size,
                        mimeType: file.type || "unknown",
                        contentType: file.type || "unknown",
                        lastModified: file.lastModified,
                        created: Date.now(),
                    }
                    uploadFileAndUpdateUser(fileData, e.target?.result as string)
                } catch (error) {
                    console.error("File upload error:", error)
                    toast.error("File upload failed")
                }
            }
            reader.readAsDataURL(file)
        }
    }

    const deleteFile = async (file: FileItem) => {
        try {
            // Prevent deletion of the root folder
            if (file._id === user.rootFolderId) {
                toast.error("Cannot delete the root folder.")
                return
            }

            // Check if the item is a folder with children
            if (file.type === "folder" && file.children && file.children.length > 0) {
                toast.error(
                    "Cannot delete a folder that contains items. Please empty the folder first."
                )
                return
            }

            if (!window.confirm(`Are you sure you want to delete "${file.name}"?`)) return

            await axios.delete(`${API_ROUTE}/files/delete-file`, {
                data: {
                    fileId: String(file._id),
                    owner: user._id,
                    authToken: user.authToken,
                },
            })
            const renew = await axios.post(`${API_ROUTE}/auth/verify-user`, {
                _id: user._id,
                authToken: user.authToken,
            })
            setUser(renew.data)
            setActiveAppSpace({})
            fetchFileTree()
        } catch (error) {
            toast.error("Failed to delete item")
        }
    }

    const createNewFolder = async () => {
        try {
            await axios.post(`${API_ROUTE}/files/create-folder`, {
                name: "New Folder",
                parentId: user.rootFolderId,
                owner: user._id,
                authToken: user.authToken,
            })
            fetchFileTree()
            toast.success("Folder created")
        } catch (error) {
            toast.error("Failed to create folder")
        }
    }

    const renameItem = async (itemId: string, newName: string) => {
        try {
            await axios.post(`${API_ROUTE}/files/rename-item`, {
                _id: itemId,
                newName,
                owner: user._id,
                authToken: user.authToken,
            })
            fetchFileTree()
            toast.success("Item renamed")
        } catch (error) {
            toast.error("Failed to rename item")
        }
    }

    const moveItem = async (itemId: string, newParentId: string) => {
        try {
            await axios.post(`${API_ROUTE}/files/move-item`, {
                _id: itemId,
                newParentId,
                owner: user._id,
                authToken: user.authToken,
            })
            fetchFileTree()
            toast.success("Item moved")
        } catch (error) {
            toast.error("Failed to move item")
        }
    }

    const downloadOriginal = async (file: FileItem) => {
        try {
            const response = await axios.get(`${API_ROUTE}/files/download-original/${file._id}`, {
                params: { owner: user._id, authToken: user.authToken },
            })
            const { content, fileName } = response.data
            const link = document.createElement("a")
            link.href = content
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            toast.error("Failed to download original file")
        }
    }

    const toggleFolderCollapse = (folderId: string) => {
        setCollapsedFolders((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(folderId)) {
                newSet.delete(folderId)
            } else {
                newSet.add(folderId)
            }
            return newSet
        })
    }

    const toggleOptions = (id: string, x: number, y: number) => {
        setShowOptions((prev) => (prev?.id === id ? null : { id, x, y }))
    }

    const renderTree = (items: FileItem[], depth: number = 1) => {
        const sortedItems = sortTree(items)
        return sortedItems.map((item) => (
            <React.Fragment key={item._id}>
                <div
                    className={styles.file}
                    style={{ marginLeft: `${depth * 2}rem` }}
                    draggable
                    onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", item._id)
                        setDragItemId(item._id)
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault()
                        if (dragItemId && dragItemId !== item._id) {
                            if (item.type === "folder") {
                                moveItem(dragItemId, item._id) // Direct folder drop
                            } else {
                                // Drop on a child: find the parent folder recursively
                                const parentFolder = findParentFolder(fileTree, item._id)
                                if (parentFolder) {
                                    moveItem(dragItemId, parentFolder._id)
                                } else {
                                    // Fallback to root if no parent found (shouldn't happen with valid tree)
                                    moveItem(dragItemId, user.rootFolderId)
                                }
                            }
                        }
                        setDragItemId(null)
                    }}
                    onClick={async (e) => {
                        e.stopPropagation()
                        if (item.type === "folder") {
                            toggleFolderCollapse(item._id)
                            return
                        }
                        try {
                            const response = await axios.get(
                                `${API_ROUTE}/files/get-file/${item._id}`
                            )
                            newAppSpace({
                                ...item,
                                fileId: item._id,
                                content: response.data.content,
                                contentType: item.contentType,
                            })
                        } catch (error) {
                            toast.error("Failed to load file content")
                        }
                    }}>
                    <div className={styles.fileContent}>
                        {item.type === "folder" ? (
                            <i
                                className="material-symbols-rounded"
                                style={{ color: "#ffb300", fontSize: "20px" }}>
                                {collapsedFolders.has(item._id) ? "folder" : "folder_open"}
                            </i>
                        ) : (
                            <div
                                style={{
                                    background: getFileColor(item.contentType || "unknown"),
                                }}
                                className={styles.fileType}></div>
                        )}
                        <span className={styles.fileName}>{truncateFileName(item.name)}</span>
                        {!readMode && (
                            <>
                                <span className={styles.info}>{bytesToReadable(item.size)}</span>
                                <span className={styles.info}>
                                    {readableFileDate(item.created)}
                                </span>
                                <i
                                    className={`material-symbols-rounded ${styles.optionsIcon}`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        toggleOptions(item._id, e.clientX, e.clientY)
                                    }}>
                                    more_horiz
                                </i>
                            </>
                        )}
                    </div>
                    {showOptions?.id === item._id && (
                        <OptionsMenu
                            item={item}
                            x={showOptions.x - 20}
                            y={showOptions.y - 20}
                            onClose={() => setShowOptions(null)}
                            onRename={(id, name) => {
                                setRenameItemId(id)
                                setNewName(name)
                            }}
                            onDelete={deleteFile}
                            onDownloadOriginal={item.originalS3Url ? downloadOriginal : undefined}
                        />
                    )}
                </div>
                {!collapsedFolders.has(item._id) &&
                    item.children &&
                    renderTree(item.children, depth + 1)}
            </React.Fragment>
        ))
    }

    useEffect(() => {
        // Allows user to create a file from Dashboard, e.g. from the "New File" button
        if (createFile) {
            createNewFile(createFile)
        }
    }, [createFile])

    const rootFolder: FileItem = fileTree.find((item) => item._id === user.rootFolderId) || {
        _id: user.rootFolderId || "",
        name: "/",
        type: "folder",
        created: Date.now(),
        lastModified: Date.now(),
        contentType: "folder",
        children: [],
    }

    return (
        <div className={styles.Files}>
            <input
                type="file"
                hidden
                ref={fileUploadRef}
                onChange={handleFileUpload}
                accept=".pdf,.docx,.xlsx,.txt,.csv,.json,image/*"
            />
            {!readMode && (
                <div className={styles.navBar}>
                    <button
                        onClick={() => {
                            if (fileUploadRef.current) fileUploadRef.current.click()
                        }}>
                        {uploading ? (
                            <div className="flex">
                                <div className="loader"></div>
                                <span>Uploading...</span>
                            </div>
                        ) : (
                            <div className="flex">
                                <i className="material-symbols-rounded">attach_file</i>
                                Upload
                            </div>
                        )}
                    </button>
                    <button onClick={createNewFolder}>
                        <i className="material-symbols-rounded">create_new_folder</i>
                        Folder
                    </button>
                    <button onClick={() => createNewFile("doc")}>
                        <i className="material-symbols-rounded">add</i>
                        Doc
                    </button>
                    <input
                        type="search"
                        className={styles.search}
                        name="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search files and folders..."
                    />
                </div>
            )}

            <div
                className={styles.file}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault()
                    if (dragItemId) {
                        moveItem(dragItemId, user.rootFolderId)
                        setDragItemId(null)
                    }
                }}
                onClick={() => toggleFolderCollapse(rootFolder._id)}>
                <div className={styles.fileContent}>
                    <i
                        className="material-symbols-rounded"
                        style={{ color: "#ffb300", fontSize: "20px" }}>
                        {collapsedFolders.has(rootFolder._id) ? "folder" : "folder_open"}
                    </i>
                    <span className={styles.fileName}>
                        {rootFolder.name === "Root" ? "/" : rootFolder.name}
                    </span>
                    {!readMode && (
                        <>
                            <span className={styles.info}>{bytesToReadable(rootFolder.size)}</span>
                            <span className={styles.info}>
                                {readableFileDate(rootFolder.created)}
                            </span>
                            <i
                                className={`material-symbols-rounded ${styles.optionsIcon}`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleOptions(rootFolder._id, e.clientX, e.clientY)
                                }}>
                                more_horiz
                            </i>
                        </>
                    )}
                </div>
                {showOptions?.id === rootFolder._id && (
                    <OptionsMenu
                        item={rootFolder}
                        x={showOptions.x - 20}
                        y={showOptions.y - 20}
                        onClose={() => setShowOptions(null)}
                        onRename={(id, name) => {
                            setRenameItemId(id)
                            setNewName(name)
                        }}
                        onDelete={deleteFile}
                    />
                )}
            </div>

            {!collapsedFolders.has(rootFolder._id) &&
                renderTree(
                    filterTree(
                        fileTree.filter((item) => item._id !== user.rootFolderId),
                        search
                    )
                )}

            {renameItemId && (
                <div
                    onClick={(e) => {
                        e.stopPropagation()
                        setRenameItemId(null)
                    }}
                    className={styles.dialogBackground}></div>
            )}

            {renameItemId && (
                <div className={styles.dialog}>
                    <h3>Rename Item</h3>
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="New name"
                    />
                    <div className={styles.dialogActions}>
                        <button
                            onClick={() => {
                                renameItem(renameItemId, newName)
                                setRenameItemId(null)
                            }}>
                            <i className="material-symbols-rounded">done_all</i>
                            Save
                        </button>
                        <button onClick={() => setRenameItemId(null)}>
                            <i className="material-symbols-rounded">close</i>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Files
